import React, { useState } from 'react';
import { Button, IconButton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { ConfirmModal } from './ConfirmModal';

interface ButtonConfirmProps {
  onClick: () => void;
  title: string;
  message: React.ReactNode;
  /** Optional secondary line below the main message (e.g. install instructions). */
  hint?: React.ReactNode;
  okText?: string;
  cancelText?: string;
  buttonText?: React.ReactNode;
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  variant?: 'button' | 'icon';
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps<Theme> | undefined;
  className?: string;
  buttonVariant?: 'text' | 'outlined' | 'contained';
  buttonColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  disabled?: boolean;
  /** Optional icon shown on the left of the modal header (e.g. a trash icon). */
  modalHeaderIcon?: React.ReactNode;
  /** Action button style. 'destructive' = red, 'primary' (default) = purple. */
  confirmTone?: 'primary' | 'destructive';
}

export const ButtonConfirm = ({
  onClick,
  title,
  message,
  hint,
  okText,
  cancelText = 'Cancel',
  buttonText,
  endIcon,
  startIcon,
  variant = 'button',
  buttonVariant = 'outlined',
  buttonColor = 'primary',
  className,
  size,
  sx,
  disabled,
  modalHeaderIcon,
  confirmTone,
}: ButtonConfirmProps) => {
  const [open, setOpen] = useState(false);

  const handleConfirm = () => {
    setOpen(false);
    onClick();
  };

  // Default to destructive when the trigger uses error color (back-compat).
  const tone: 'primary' | 'destructive' =
    confirmTone ?? (buttonColor === 'error' ? 'destructive' : 'primary');

  return (
    <>
      {variant === 'button' ? (
        <Button
          size={size || undefined}
          variant={buttonVariant}
          color={buttonColor}
          onClick={() => setOpen(true)}
          disabled={disabled}
          endIcon={endIcon || null}
          startIcon={startIcon || null}
          sx={{ ...sx }}
          className={className}
        >
          {buttonText}
        </Button>
      ) : (
        <IconButton
          size={size || 'small'}
          color={buttonColor}
          className="icon-button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          sx={sx}
        >
          {endIcon}
        </IconButton>
      )}

      <ConfirmModal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
        title={title}
        message={message}
        hint={hint}
        okText={okText}
        cancelText={cancelText}
        tone={tone}
        headerIcon={modalHeaderIcon}
      />
    </>
  );
};
